import React, { useState, useEffect, useRef } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import { useAuth } from '../auth/AuthContext'
import {
  dashboardService,
  type ConversionStats,
  type ConnectionPair,
} from './dashboardService'
import type {
  StatsFilters,
  OverviewStats,
  LineupStatsRow,
  OffenseBreakdown,
  DefenseBreakdown,
  MatchOffenseRow,
  ConceptBreakdownRow,
  CallBreakdownRow,
} from '../../types/sportsStats'
import {
  Zap,
  Shield,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'

interface TeamSportPerformanceViewProps {
  onNavigateToStats?: () => void
  teamId?: string
  camp?: string
  from?: string
  to?: string
}

export const TeamSportPerformanceView: React.FC<TeamSportPerformanceViewProps> = ({
  onNavigateToStats,
  teamId: propTeamId,
  camp,
  from,
  to,
}) => {
  let user = null
  try {
    const auth = useAuth()
    user = auth?.user ?? null
  } catch {
    user = null
  }
  const teamId = propTeamId || user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'

  const [selectedUnit, setSelectedUnit] = useState<'offense' | 'defense'>('offense')
  const [overview, setOverview] = useState<OverviewStats | null>(null)
  const [lineups, setLineups] = useState<LineupStatsRow[]>([])
  const [offenseBreakdown, setOffenseBreakdown] = useState<OffenseBreakdown | null>(null)
  const [defenseBreakdown, setDefenseBreakdown] = useState<DefenseBreakdown | null>(null)
  const [conversions, setConversions] = useState<ConversionStats | null>(null)
  const [connections, setConnections] = useState<ConnectionPair[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [showConversionSnaps, setShowConversionSnaps] = useState<boolean>(false)

  const activeRequestId = useRef(0)

  useEffect(() => {
    let isMounted = true
    const reqId = ++activeRequestId.current

    async function loadStats() {
      setLoading(true)
      try {
        const filters: StatsFilters = {
          eventId: camp || undefined,
          startDate: from || undefined,
          endDate: to || undefined,
        }

        const [
          overviewData,
          lineupsData,
          offenseData,
          defenseData,
          convData,
          connData,
        ] = await Promise.all([
          dashboardService.getOverview(teamId, filters).catch(() => null),
          dashboardService.getLineups(teamId, filters).catch(() => []),
          dashboardService.getOffenseBreakdown(teamId, filters).catch(() => null),
          dashboardService.getDefenseBreakdown(teamId, filters).catch(() => null),
          dashboardService.getConversionStats(teamId).catch(() => ({
            attempts_1pt: 0,
            made_1pt: 0,
            pct_1pt: null,
            attempts_2pt: 0,
            made_2pt: 0,
            pct_2pt: null,
            total_attempts: 0,
            total_made: 0,
            total_pct: null,
            total_points: 0,
            snaps: [],
            by_player: [],
          })),
          dashboardService.getConnections(teamId, filters).catch(() => []),
        ])

        if (!isMounted || activeRequestId.current !== reqId) return
        setOverview(overviewData)
        setLineups(lineupsData || [])
        setOffenseBreakdown(offenseData)
        setDefenseBreakdown(defenseData)
        setConversions(convData)
        setConnections(connData)
      } catch (err) {
        console.warn('Error cargando estadísticas deportivas de equipo:', err)
      } finally {
        if (isMounted && activeRequestId.current === reqId) {
          setLoading(false)
        }
      }
    }

    loadStats()
    return () => {
      isMounted = false
    }
  }, [teamId, camp, from, to])

  // Métricas reales de ataque
  const offSnaps = overview?.snaps.offense || 0
  const offNetYards = overview?.offense.known_net_yards || 0
  const offYardsPerSnap = offSnaps > 0 ? Number((offNetYards / offSnaps).toFixed(2)) : null
  const passAtt = overview?.offense.attempts || 0
  const passCmp = overview?.offense.completions || 0
  const passCompPct = overview?.offense.comp_pct ?? (passAtt > 0 ? Math.round((passCmp / passAtt) * 100) : null)
  const passTds = overview?.offense.pass_tds || 0
  const rushTds = overview?.offense.rush_tds || 0
  const totalTds = passTds + rushTds
  const turnoversLost = (overview?.offense as any)?.turnovers || 0

  // Métricas reales de defensa
  const defSnaps = overview?.snaps.defense || 0
  const defFlagsOk = overview?.defense.flags_success || 0
  const defFlagsMiss = overview?.defense.flags_miss || 0
  const totalFlags = defFlagsOk + defFlagsMiss
  const flagPct = overview?.defense.flag_pct ?? (totalFlags > 0 ? Math.round((defFlagsOk / totalFlags) * 100) : null)
  const defSacks = overview?.defense.sacks || 0
  const defPressures = overview?.defense.pressures || 0
  const defInts = overview?.defense.interceptions || 0
  const defTds = overview?.defense.tds_scored || 0

  // Partidos / Scrimmages reales de la evolución
  const matchEvolutionData = (offenseBreakdown?.by_match || []).map((m: MatchOffenseRow) => ({
    id: m.match_id,
    label: m.match_title.length > 15 ? m.match_title.slice(0, 15) + '…' : m.match_title,
    fullTitle: m.match_title,
    snaps: m.snaps,
    yardsPerSnap: m.snaps > 0 ? Number((m.yards / m.snaps).toFixed(2)) : 0,
    completions: m.completions,
    attempts: m.attempts,
    tds: m.tds,
  }))

  if (loading && !overview) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted, #64748b)' }}>
        Cargando rendimiento deportivo del equipo...
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Unit Selector & Context Strip */}
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '0.9rem 1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                background: 'var(--brand-red, #b91c1c)',
                color: '#ffffff',
                fontSize: '0.7rem',
                fontWeight: 800,
                padding: '2px 8px',
                borderRadius: '4px',
                textTransform: 'uppercase',
              }}
            >
              Rendimiento Deportivo · M7 & M8C
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #64748b)' }}>
              Registros consolidados de campo (Flag Football 5v5)
            </span>
          </div>
          <h2
            style={{
              margin: '4px 0 0 0',
              fontSize: '1.25rem',
              fontWeight: 800,
              color: 'var(--text-main, #0f172a)',
              letterSpacing: '-0.02em',
            }}
          >
            Análisis Táctico y Estadísticas Colectivas
          </h2>
        </div>

        {/* Unit Toggle */}
        <div
          style={{
            display: 'inline-flex',
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '3px',
          }}
        >
          <button
            type="button"
            onClick={() => setSelectedUnit('offense')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              background: selectedUnit === 'offense' ? 'var(--nav-bg-base, #0a1320)' : 'transparent',
              color: selectedUnit === 'offense' ? '#ffffff' : 'var(--text-secondary, #475569)',
              fontSize: '0.82rem',
              fontWeight: selectedUnit === 'offense' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <Zap size={14} color={selectedUnit === 'offense' ? '#f59e0b' : '#64748b'} />
            <span>Unidad de Ataque</span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedUnit('defense')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              background: selectedUnit === 'defense' ? 'var(--nav-bg-base, #0a1320)' : 'transparent',
              color: selectedUnit === 'defense' ? '#ffffff' : 'var(--text-secondary, #475569)',
              fontSize: '0.82rem',
              fontWeight: selectedUnit === 'defense' ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <Shield size={14} color={selectedUnit === 'defense' ? '#3b82f6' : '#64748b'} />
            <span>Unidad de Defensa</span>
          </button>
        </div>
      </div>

      {/* KPI Cards Row (Datos Reales M7) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.85rem',
        }}
      >
        {selectedUnit === 'offense' ? (
          <>
            {/* Offense KPI 1: Snaps Ofensivos */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Snaps Ofensivos
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {offSnaps} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>snaps</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {offSnaps > 0 ? `n=${offSnaps} jugadas válidas` : 'Sin jugadas registradas'}
              </div>
            </div>

            {/* Offense KPI 2: Ganancia Neta / Snap */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Ganancia Neta / Snap
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                {offYardsPerSnap !== null ? `${offYardsPerSnap}` : 'N/D'}{' '}
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>yd / jugada</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {offNetYards} yardas netas totales
              </div>
            </div>

            {/* Offense KPI 3: Pases Completos / Intentos */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Completos / Intentos
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {passCompPct !== null ? `${passCompPct}%` : 'N/D'}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {passCmp} / {passAtt} pases
              </div>
            </div>

            {/* Offense KPI 4: Touchdowns Ordinarios */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Touchdowns Anotados
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#b91c1c', fontVariantNumeric: 'tabular-nums' }}>
                {totalTds} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>TD</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {passTds} pase · {rushTds} carrera (6 pts c/u)
              </div>
            </div>

            {/* Offense KPI 5: Pérdidas de Balón */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Pérdidas de Balón
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#d97706', fontVariantNumeric: 'tabular-nums' }}>
                {turnoversLost} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>turnovers</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {turnoversLost > 0 ? `${turnoversLost} cambios de posesión` : '0 pérdidas registradas'}
              </div>
            </div>

            {/* Offense KPI 6: Eficiencia 3º/4º Down & Zona Roja */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Eficiencia 3º/4º Down
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-secondary, #475569)', fontVariantNumeric: 'tabular-nums' }}>
                No disponible
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '4px' }}>
                No disponible con el registro actual
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Defense KPI 1: Snaps Defensivos */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Snaps Defensivos
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {defSnaps} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>snaps</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {defSnaps > 0 ? `n=${defSnaps} snaps oposición` : 'Sin registros defensivos'}
              </div>
            </div>

            {/* Defense KPI 2: Efectividad de Flags */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Efectividad de Flags
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {flagPct !== null ? `${flagPct}%` : 'N/D'}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {defFlagsOk} de {totalFlags} flags conseguidos
              </div>
            </div>

            {/* Defense KPI 3: Sacks y Presiones */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Sacks y Presiones
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#d97706', fontVariantNumeric: 'tabular-nums' }}>
                {defSacks} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>sacks</span> · {defPressures}{' '}
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>pres.</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                No excluyentes en un mismo snap
              </div>
            </div>

            {/* Defense KPI 4: Intercepciones */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Intercepciones
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                {defInts} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>INT</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                {defTds} anotaciones defensivas
              </div>
            </div>

            {/* Defense KPI 5: Tasa Blitz */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Tasa Disrupción Blitz
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-secondary, #475569)', fontVariantNumeric: 'tabular-nums' }}>
                {defSnaps > 0 ? `${(((defSacks + defPressures) / defSnaps) * 100).toFixed(1)}%` : 'N/D'}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '2px' }}>
                Sobre total snaps defensivos
              </div>
            </div>

            {/* Defense KPI 6: Zona Roja Rival */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Paradas en Zona Roja
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-secondary, #475569)', fontVariantNumeric: 'tabular-nums' }}>
                No disponible
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '4px' }}>
                No disponible con el registro actual
              </div>
            </div>
          </>
        )}
      </div>

      {/* BLOQUE DESTACADO M8C: CONVERSIONES DE 1 Y 2 PUNTOS */}
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1.5px solid rgba(234, 88, 12, 0.4)',
          borderRadius: '10px',
          padding: '1.25rem',
          boxShadow: '0 2px 8px rgba(234, 88, 12, 0.08)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '8px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                style={{
                  backgroundColor: '#ea580c',
                  color: '#ffffff',
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                }}
              >
                M8C · Extras
              </span>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Conversiones de 1 y 2 Puntos (Puntos Extra)
              </h3>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.76rem', color: 'var(--text-secondary, #475569)' }}>
              Registro exclusivo e independiente de jugadas post-TD. No se mezcla con yardas de scrimmage ni suma falsos TDs.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#ea580c' }}>
              {conversions?.total_points || 0} Puntos Totales Extra
            </span>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowConversionSnaps(!showConversionSnaps)}
              style={{ fontSize: '0.75rem', padding: '4px 10px' }}
            >
              <span>{showConversionSnaps ? 'Ocultar snaps' : `Ver ${conversions?.snaps.length || 0} snaps`}</span>
              {showConversionSnaps ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          </div>
        </div>

        {/* Tarjetas de conversiones de 1 y 2 puntos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          {/* Conversiones de 1 Punto */}
          <div
            style={{
              background: 'var(--surface-subtle, #f8fafc)',
              border: '1px solid var(--surface-border, #e2e8f0)',
              borderRadius: '8px',
              padding: '0.9rem 1rem',
            }}
          >
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Conversiones de 1 Punto (Yd 45)
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
              {conversions?.pct_1pt !== null ? `${conversions?.pct_1pt}%` : 'N/D'}{' '}
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>
                ({conversions?.made_1pt || 0} / {conversions?.attempts_1pt || 0} conseguidas)
              </span>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 600, marginTop: '2px' }}>
              +{conversions?.made_1pt || 0} puntos sumados al marcador
            </div>
          </div>

          {/* Conversiones de 2 Puntos */}
          <div
            style={{
              background: 'var(--surface-subtle, #f8fafc)',
              border: '1px solid var(--surface-border, #e2e8f0)',
              borderRadius: '8px',
              padding: '0.9rem 1rem',
            }}
          >
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Conversiones de 2 Puntos (Yd 40)
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
              {conversions?.pct_2pt !== null ? `${conversions?.pct_2pt}%` : 'N/D'}{' '}
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>
                ({conversions?.made_2pt || 0} / {conversions?.attempts_2pt || 0} conseguidas)
              </span>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 600, marginTop: '2px' }}>
              +{(conversions?.made_2pt || 0) * 2} puntos sumados al marcador
            </div>
          </div>

          {/* Eficiencia Global */}
          <div
            style={{
              background: 'var(--surface-subtle, #f8fafc)',
              border: '1px solid var(--surface-border, #e2e8f0)',
              borderRadius: '8px',
              padding: '0.9rem 1rem',
            }}
          >
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Efectividad Global Conversiones
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#ea580c', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
              {conversions?.total_pct !== null ? `${conversions?.total_pct}%` : 'N/D'}{' '}
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>
                ({conversions?.total_made || 0} / {conversions?.total_attempts || 0})
              </span>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
              Tasa de éxito sobre intentos válidos
            </div>
          </div>
        </div>

        {/* Desglose individual de conversiones por atleta */}
        {conversions?.by_player && conversions.by_player.length > 0 && (
          <div style={{ marginBottom: '1rem', background: '#ffffff', borderRadius: '6px', border: '1px solid #e2e8f0', padding: '0.75rem 1rem' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
              Anotadores y Ejecutores de Conversión
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {conversions.by_player.map((p) => (
                <div
                  key={p.athlete_id}
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                  }}
                >
                  <strong>{p.name}:</strong> {p.made}/{p.attempts} ({p.points} pts)
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Drill-down desplegable a los snaps de conversión */}
        {showConversionSnaps && (
          <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Secuencia</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Valor Intentado</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Resultado</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Puntos Otorgados</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Yarda Inicio</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Pasador / Ejecutor</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Fecha Registro</th>
                </tr>
              </thead>
              <tbody>
                {conversions?.snaps && conversions.snaps.length > 0 ? (
                  conversions.snaps.map((s) => (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 700 }}>Snap #{s.sequence}</td>
                      <td style={{ padding: '6px 8px' }}>{s.conversion_points_attempted} Punto{s.conversion_points_attempted === 2 ? 's' : ''}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '1px 6px',
                            borderRadius: '3px',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            background: s.conversion_result === 'successful' ? '#dcfce7' : '#fee2e2',
                            color: s.conversion_result === 'successful' ? '#15803d' : '#991b1b',
                          }}
                        >
                          {s.conversion_result === 'successful' ? 'Conseguida' : 'Fallada'}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', fontWeight: 800, color: s.points_awarded > 0 ? '#15803d' : '#64748b' }}>
                        +{s.points_awarded}
                      </td>
                      <td style={{ padding: '6px 8px' }}>Yarda {s.yard_line_start || 45}</td>
                      <td style={{ padding: '6px 8px' }}>{s.receiver_name || s.passer_name || '—'}</td>
                      <td style={{ padding: '6px 8px', color: '#64748b' }}>{new Date(s.registered_at).toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} style={{ padding: '12px', textAlign: 'center', color: '#64748b' }}>
                      Sin intentos de conversión registrados en los partidos actuales
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Gráficos Principales: Evolución por Jornada */}
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Evolución de Rendimiento por Jornada Real
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: 'var(--text-secondary, #475569)' }}>
              Ganancia neta por snap a lo largo de partidos y bloques registrados
            </p>
          </div>
        </div>

        {matchEvolutionData.length > 0 ? (
          <div style={{ width: '100%', height: '220px', marginTop: '0.5rem' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={matchEvolutionData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: '#0a1320', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.75rem' }}
                  formatter={(val: any, _name: any, item: any) => [
                    `${val} yd/snap (${item.payload.snaps} snaps, ${item.payload.tds} TDs)`,
                    item.payload.fullTitle,
                  ]}
                />
                <Bar dataKey="yardsPerSnap" fill="var(--brand-red, #b91c1c)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.82rem' }}>
            Sin histórico de jornadas suficientes para graficar la evolución. Registra partidos en Captura y Deporte.
          </div>
        )}
      </div>

      {/* Desglose por Concepto Ofensivo o Llamada Defensiva (Base, Dinámico, Hydra, Standard) */}
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              {selectedUnit === 'offense' ? 'Rendimiento por Concepto Ofensivo' : 'Efectividad por Cobertura Defensiva'}
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              {selectedUnit === 'offense'
                ? 'Comportamiento táctico en jugadas de catálogo Base, Dinámico, Hydra y Standard'
                : 'Métricas de parada y yardas permitidas según la llamada defensiva de catálogo'}
            </p>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
            <thead style={{ background: 'var(--surface-subtle, #f8fafc)', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b' }}>
                  {selectedUnit === 'offense' ? 'Concepto' : 'Llamada Defensiva'}
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>Snaps (n)</th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>
                  {selectedUnit === 'offense' ? 'Yd / Snap' : 'Sacks'}
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>
                  {selectedUnit === 'offense' ? '% Completos' : 'Intercepciones'}
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', textAlign: 'right' }}>
                  {selectedUnit === 'offense' ? 'Touchdowns' : 'Flags OK'}
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedUnit === 'offense' ? (
                offenseBreakdown?.by_concept && offenseBreakdown.by_concept.length > 0 ? (
                  offenseBreakdown.by_concept.map((c: ConceptBreakdownRow) => (
                    <tr key={c.concept_name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: '#0f172a' }}>{c.concept_name}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.snaps}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                        {c.snaps > 0 ? (c.yards / c.snaps).toFixed(1) : '—'} yd
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {c.comp_pct !== null ? `${c.comp_pct}%` : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.tds} TD</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ padding: '12px', textAlign: 'center', color: '#64748b' }}>
                      Sin conceptos ofensivos registrados aún
                    </td>
                  </tr>
                )
              ) : (
                defenseBreakdown?.by_call && defenseBreakdown.by_call.length > 0 ? (
                  defenseBreakdown.by_call.map((d: CallBreakdownRow) => (
                    <tr key={d.call_name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: '#0f172a' }}>{d.call_name}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.snaps}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.sacks}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.interceptions} INT</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
                        {d.flags_ok}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ padding: '12px', textAlign: 'center', color: '#64748b' }}>
                      Sin llamadas defensivas registradas aún
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CONEXIONES PASADOR-RECEPTOR REALES (Solo en Unidad de Ataque) */}
      {selectedUnit === 'offense' && (
        <div
          style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '10px',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Conexiones Pasador Real – Receptor
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
                Parejas reales de pases con volumen y conceptos incluidos (Hydra, Base, Dinámico). Muestra visible; no se asume causalidad con muestras pequeñas.
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
            {connections.length > 0 ? (
              connections.map((c) => (
                <div
                  key={`${c.passer_id}:${c.receiver_id}`}
                  style={{
                    background: 'var(--surface-subtle, #f8fafc)',
                    border: '1px solid var(--surface-border, #e2e8f0)',
                    borderRadius: '6px',
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                      {c.passer_name} ➔ {c.receiver_name}
                    </div>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#ea580c' }}>
                      {c.tds} TD
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', color: '#64748b' }}>
                    <span>
                      {c.completions}/{c.targets} ({c.comp_pct ?? 0}%)
                    </span>
                    <span>{c.yards} yardas ganadas</span>
                  </div>

                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '4px' }}>
                    Conceptos: {Object.keys(c.concepts).join(', ') || 'Standard'} · n={c.targets}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ gridColumn: '1 / -1', padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
                Sin conexiones de pase registradas aún en el equipo
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quintetos Reales por Volumen en Campo (Filtrados por la unidad activa) */}
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Quintetos Destacados de {selectedUnit === 'offense' ? 'Ataque' : 'Defensa'}
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              Combinaciones canónicas congeladas de 5 atletas en campo para la unidad de {selectedUnit === 'offense' ? 'ataque' : 'defensa'}.
            </p>
          </div>

          {onNavigateToStats && (
            <button
              type="button"
              onClick={onNavigateToStats}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'var(--brand-red, #b91c1c)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span>Ver todas en Rendimiento Deportivo</span>
              <ArrowRight size={12} />
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {lineups.filter((l) => l.unit === selectedUnit).length > 0 ? (
            lineups
              .filter((l) => l.unit === selectedUnit)
              .slice(0, 5)
              .map((l, idx) => (
                <div
                  key={l.lineup_hash || idx}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                      {l.athlete_names.join(', ') || 'Quinteto en campo'}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                      Muestra real: <strong>{l.snap_count} snaps</strong> en {l.match_count} partidos ({selectedUnit === 'offense' ? 'Ataque' : 'Defensa'})
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {selectedUnit === 'offense' && l.offense_metrics && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Efectividad Pase</div>
                        <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#15803d' }}>
                          {l.offense_metrics.comp_pct !== null ? `${l.offense_metrics.comp_pct}%` : '—'}
                        </div>
                      </div>
                    )}

                    {selectedUnit === 'defense' && l.defense_metrics && (
                      <div style={{ display: 'flex', gap: '14px', textAlign: 'right' }}>
                        <div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Flags / Paradas</div>
                          <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
                            {l.defense_metrics.flags_ok} ok {l.defense_metrics.flag_pct !== null ? `(${l.defense_metrics.flag_pct}%)` : ''}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Sacks / INT</div>
                          <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#15803d' }}>
                            {l.defense_metrics.sacks} sck · {l.defense_metrics.interceptions} INT
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
          ) : (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
              Sin combinaciones de quinteto de {selectedUnit === 'offense' ? 'ataque' : 'defensa'} registradas con suficiente volumen
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
export default TeamSportPerformanceView
